import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    selectedBillTo?: BillToModel;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isAuthenticated, isGuest } = getSession(state);
    return {
        // selectedBillTo: state.components.addressDrawer.selectedBillTo,
        showSelectedBillTo: isAuthenticated && !isGuest,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url,
    };
};

const mapDispatchToProps = {
    setDrawerIsOpen,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SelectedBilltoCustomerStyles {
    companyNameText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    customerNameText?: TypographyPresentationProps;
    signInLink?: InjectableCss;
    createAccountLink?: InjectableCss;
    signInGridItem?: GridItemProps;
}

export const selectedBilltoCustomerStyles: SelectedBilltoCustomerStyles = {
    wrapper: {
        css: css`
            font-style: normal;
            padding: 12px 8px;
            p {
                font-size: 12px;
            }
        `,
    },
    companyNameText: {
        weight: 500,
        css: css`
            font-size: 12px;
        `,
    },
    customerNameText: {
        css: css`
            padding-bottom: 16px;
        `,
    },
    signInLink: {
        css: css`
            font-weight: 450;
            color: ${getColor("text.link")};
            padding-right: 5px;
            max-width: max-content;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css``,
                    css``,
                    css`
                        padding-left: 10px;
                    `,
                ])}
        `,
    },
    createAccountLink: {
        css: css`
            font-weight: 450;
            color: ${getColor("text.link")};
            padding-right: 5px;
            padding-left: 5px;
            max-width: max-content;
        `,
    },
    signInGridItem: {
        width: 12,
        css: css`
            font-size: 12px;
            justify-content: center;
        `,
    },
};
const styles = selectedBilltoCustomerStyles;
const StyledAddress = getStyledWrapper("address");

const SelectedBillToCustomer = ({
    selectedBillTo,
    setDrawerIsOpen,
    showSelectedBillTo,
    signInUrl,
    createAccountUrl,
}: Props) => {
    const messageTemplate = translate("{0} or {1} to select Ship-To address");
    const parts = messageTemplate.split(/({\d})/);
    return (
        <>
            {showSelectedBillTo ? (
                <StyledAddress {...styles.wrapper} data-test-selector="selectedAddressInfoDisplay">
                    {selectedBillTo?.customerNumber && (
                        <Typography as="p" {...styles.companyNameText}>
                            {`#${selectedBillTo?.customerNumber}`}
                        </Typography>
                    )}
                    {selectedBillTo?.properties?.customerName && (
                        <Typography as="p" {...styles.customerNameText}>
                            {selectedBillTo?.properties?.customerName}
                        </Typography>
                    )}

                    {selectedBillTo?.companyName && (
                        <Typography as="p" {...styles.companyNameText}>
                            {selectedBillTo?.companyName}
                        </Typography>
                    )}
                    <Typography as="p">{selectedBillTo?.address1}</Typography>
                    <Typography as="p">
                        {`${selectedBillTo?.city}, ${selectedBillTo?.state?.name}, ${selectedBillTo?.postalCode}`}
                    </Typography>
                </StyledAddress>
            ) : (
                <Typography
                    as="p"
                    style={{ display: "flex", flexWrap: "wrap", alignItems: "center", fontSize: "12px" }}
                >
                    {parts.map((part, index) => {
                        switch (part) {
                            case "{0}":
                                return (
                                    <Clickable
                                        key={index}
                                        {...styles.signInLink}
                                        href={signInUrl}
                                        onClick={() => setDrawerIsOpen({ isOpen: false })}
                                    >
                                        {translate("Log-in")}
                                    </Clickable>
                                );
                            case "{1}":
                                return (
                                    <Clickable
                                        key={index}
                                        {...styles.createAccountLink}
                                        href={createAccountUrl}
                                        onClick={() => setDrawerIsOpen({ isOpen: false })}
                                    >
                                        {translate("Create Account")}
                                    </Clickable>
                                );
                            default:
                                return <React.Fragment key={index}>{part}</React.Fragment>;
                        }
                    })}
                </Typography>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectedBillToCustomer);
